import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { drawClover, drawHeart } from './holidayShapes';
import { random, useCanvasAnimation } from './useCanvasAnimation';

type FloatingParticlesProps = {
  shape: 'heart' | 'clover';
  direction: 'up' | 'down';
  colors: string[];
  count?: number;
  /** replay the drift this often (ms) for as long as the page is open */
  repeatEvery?: number;
};

const THREE_MINUTES = 3 * 60 * 1000;

/**
 * A handful of shapes drift across the screen (hearts float up, clovers fall), then the canvas goes idle until the
 * next drift. Mounted once at the app level, so route changes don't restart it.
 */
export const FloatingParticles = ({
  shape,
  direction,
  colors,
  count = 16,
  repeatEvery = THREE_MINUTES,
}: FloatingParticlesProps) => {
  const [runKey, setRunKey] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setRunKey((key) => key + 1), repeatEvery);
    return () => clearInterval(interval);
  }, [repeatEvery]);

  const canvasRef = useCanvasAnimation({
    runKey,
    createStep: ({ width, height }) => {
      const sign = direction === 'up' ? -1 : 1;
      const particles = Array.from({ length: count }, (_, i) => ({
        x: random(16, width - 16),
        // start just off screen, staggered so they don't arrive as one wall
        y: direction === 'up' ? height + random(10, height * 0.8) : -random(10, height * 0.8),
        size: random(7, 13),
        speed: random(0.07, 0.13),
        phase: random(0, Math.PI * 2),
        rotation: random(0, Math.PI * 2),
        spin: random(-0.002, 0.002),
        color: colors[i % colors.length],
        // rising shapes are fully faded by `fadeEnd`; most make it near the top, but some give out partway up
        ...(Math.random() < 0.45
          ? { fadeEnd: random(height * 0.3, height * 0.65), fadeLength: random(80, 180) }
          : { fadeEnd: 0, fadeLength: height * 0.4 }),
      }));
      let elapsed = 0;

      return (ctx, dt) => {
        elapsed += dt;
        let visible = 0;
        for (const p of particles) {
          p.y += sign * p.speed * dt;
          p.x += Math.sin(elapsed / 650 + p.phase) * 0.5;
          p.rotation += p.spin * dt;
          const onScreen = direction === 'up' ? p.y > p.fadeEnd - 30 : p.y < height + 30;
          if (!onScreen) continue;
          visible++;
          ctx.save();
          ctx.globalAlpha = direction === 'up' ? Math.max(0, Math.min(0.85, (p.y - p.fadeEnd) / p.fadeLength)) : 0.85;
          ctx.translate(p.x, p.y);
          ctx.rotate(shape === 'heart' ? Math.sin(elapsed / 500 + p.phase) * 0.25 : p.rotation);
          ctx.fillStyle = p.color;
          if (shape === 'heart') drawHeart(ctx, 0, 0, p.size);
          else drawClover(ctx, 0, 0, p.size);
          ctx.restore();
        }
        return visible > 0;
      };
    },
  });

  return <Box component='canvas' ref={canvasRef} aria-hidden sx={sxStyles.canvas} />;
};

const sxStyles = {
  canvas: {
    position: 'fixed',
    inset: 0,
    width: '100vw',
    height: '100vh',
    pointerEvents: 'none',
    // above page content, below MUI modals (1300)
    zIndex: 1250,
  },
};
